import { useMemo } from 'react'

import { UserPermissions } from '@/config/permissions/user-permissions'
import useHasPermissions from '@/hooks/use-has-permissions'
import {
  useGetSelfQuery,
  useListOrganizationUsersQuery,
} from '@/legacy-api/user-slice'

export const UNASSIGNED_TRADER_ID = 'unassigned'

/**
 * Returns a list of options for the trader select, if the user has permission to read users.
 * Otherwise, returns null.
 * @returns {Array<{value: string, label: string}> | null}
 */

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function useGetTraderOptions(): Array<{
  value: string
  label: string
}> | null {
  const { data: selfData } = useGetSelfQuery()
  const organizationId = selfData?.organization_id || ''
  const hasUserReadPermission = useHasPermissions(UserPermissions.READ)
  const { data: usersData = { data: [] } } = useListOrganizationUsersQuery(
    {
      organizationId,
    },
    {
      skip: !organizationId || !hasUserReadPermission,
    },
  )

  // TODO: Technically this is just returning a list of users, so we need to eventually filter down to just the users that are traders
  const traderOptions = useMemo(() => {
    const options = usersData.data
      .map((user) => transformUserToOption(user, selfData?.id))
      .sort(sortWithCurrentUserFirst(selfData?.id))
    options.unshift({
      value: UNASSIGNED_TRADER_ID,
      label: 'Unassigned',
    })
    return options
  }, [usersData, selfData?.id])

  return traderOptions
}

// biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
const transformUserToOption = (user: any, selfId?: string) => {
  const fullName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.email

  const isCurrentUser = user.id === selfId
  const label = isCurrentUser ? `Me (${fullName})` : fullName

  return {
    value: user.id,
    label,
  }
}

const sortWithCurrentUserFirst = (selfId?: string) => {
  return (
    a: { value: string; label: string },
    b: { value: string; label: string },
  ) => {
    const isUserA = a.value === selfId
    const isUserB = b.value === selfId

    if (isUserA) return -1
    if (isUserB) return 1

    return a.label.localeCompare(b.label)
  }
}
